import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, CanLoad, Route, Router } from '@angular/router';
import { logFeature } from '@office-start/telemetry-service';
import { KnownFeatureInstrumentationEvents, KnownFeatureNames } from '@office-start/telemetry-types';

import { LinkTarget } from '../shared/constants/link-target.enum';
import { InstrumentationService, KnownInstrumentationEvents } from '../shared/instrumentation/instrumentation.service';
import { FileType } from '../shared/models/file-item.model';
import { SchedulePriority } from '../shared/services/scheduler.service';
import { ShellService } from '../shared/services/shell.service';
import { LaunchAction, UrlLauncherService } from '../shared/services/url-launcher.service';
import { WorkloadsService } from '../shared/services/workloads.service';
import { CapabilitiesService } from '../shared/shared-context/capabilities.service';
import { BaseRouteGuard } from './base.route-guard';

@Injectable({
    providedIn: 'root'
})
export class ODSPStartRouteGuard extends BaseRouteGuard implements CanActivate, CanLoad {
    constructor(
        capabilitiesService: CapabilitiesService,
        public readonly shellService: ShellService,
        router: Router,
        public readonly urlLauncherService: UrlLauncherService,
        public readonly workloadService: WorkloadsService,
        public readonly instrumentationService: InstrumentationService
    ) {
        super(router, capabilitiesService);
    }

    public canActivate(activatedRoute: ActivatedRouteSnapshot) {
        const fileType = activatedRoute.data['fileType'];
        return this.provisioningCheckHelper(fileType);
    }

    public canLoad(route: Route) {
        const fileType = route.data['fileType'];
        return this.provisioningCheckHelper(fileType);
    }

    public provisioningCheckHelper(fileType: FileType): Promise<boolean> | boolean {
        // A true value on this.router.navigated signals that the navigation was made within the angular application, false otherwise.
        // When users navigate to the page from office.com, we want to open a new tab, otherwise if they are redirected to office.com in anyway (new tab or same tab) we want to prevent the
        // guard from opening up another new tab -preventing a double new tab situation.
        const linkTarget = this.router.navigated ? LinkTarget.NewTab : LinkTarget.SameFrame;
        if (!this.checkEnabledAndRedirect(this.capabilitiesService.capabilities.startPageApps.has(fileType))) {
            return false;
        }
        return new Promise(resolve => {
            // If someone navigates directly to the ODSP launch page through a link from an external webpage, it will hit this route and we have to check if we should make the call or use the getShellInfo link provided if the user is already provisioend on all clusters.
            this.shellService.checkInAppNavEnabled(fileType).then( enabled => {
                if (enabled) {
                    // Schedule High and 1 Second timeout limit, if timeout hits, we go to landing page. We don't want to block user interaction.
                    this.shellService.getOdspProvisioningStatus(SchedulePriority.Immediate, 1000).then(res => {
                        const url = fileType === FileType.OneDrive ? res.OneDriveUrl : res.SharepointUrl;
                        if (url) {
                            // Stop navigating to ODSP Provisioning start page and go directly to the onedrive or sharepoint landing page in url.
                            resolve(false);
                            // Logs the moment when the provisioning status of ODSP has not propogated to the shell in the shellData yet but has already been provisioned and the user has clicked on the onedrive or sharepoint tile on shell or office.com.
                            this.instrumentationService.logGenericEvent(KnownInstrumentationEvents.ODSPInstaProvisionedRedirect);
                            logFeature({featureName: KnownFeatureNames.ODSP, eventName: KnownFeatureInstrumentationEvents.ODSPInstaProvisionedRedirect });
                            this.urlLauncherService.openUrl(url, LaunchAction.ODSPRedirect, linkTarget);
                        } else {
                            // Continue going to ODSP provisioning start page.
                            resolve(true);
                        }
                    }).catch(() => {
                        // Continue going to ODSP provisioning start page.
                        resolve(true);
                    });
                } else {
                    // If inApp Nav is not enabled for the specific workload, then that means that getShellInfo knows that the user has been provisioned for this workload on all clusters, (sharepoint and onedrive) thus,
                    // we will use the url provided to redirect directly to the workload's landing page.
                    this.workloadService.getWorkloads().then(ws => {
                        const workload = ws.find(w => w.FileType === fileType);
                        if (workload) {
                            this.urlLauncherService.openUrl(workload.Url, LaunchAction.ODSPRedirect, linkTarget);
                        } else {
                            this.checkEnabledAndRedirect(false);
                        }
                        resolve(false);
                    });
                }
            });
        });
    }
}
